import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';
import { Button } from './Button';

type CopyState = 'idle' | 'copied' | 'failed';

/** Copies text and says so in its own polite live region (no Announcer needed). */
export function CopyButton({ value, label }: { value: string; label: string }): ReactElement {
  const [state, setState] = useState<CopyState>('idle');
  const timer = useRef<number | null>(null);

  const clearTimer = useCallback(() => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);
  useEffect(() => clearTimer, [clearTimer]);

  const settle = useCallback(
    (next: CopyState) => {
      clearTimer();
      setState(next);
      timer.current = window.setTimeout(() => {
        timer.current = null;
        setState('idle');
      }, 1600);
    },
    [clearTimer]
  );

  const copy = useCallback(() => {
    void navigator.clipboard.writeText(value).then(
      () => settle('copied'),
      () => settle('failed')
    );
  }, [value, settle]);

  const text = state === 'copied' ? 'Copied' : state === 'failed' ? 'Copy failed' : label;
  const announcement =
    state === 'copied' ? `${label}: copied to the clipboard` : state === 'failed' ? `${label}: copy failed` : '';
  return (
    <Button size="sm" onPress={copy}>
      {text}
      <span className="sr-only" aria-live="polite">{announcement}</span>
    </Button>
  );
}
